import React, { useState, useEffect, useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import { X, Plus, Trash2, MapPin, Clock, Calendar, DollarSign } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Route, CreateRouteForm, UpdateRouteForm } from '../../types';

// Add global declaration for google maps
declare global {
  interface Window {
    google: any;
  }
  namespace JSX {
    interface IntrinsicElements {
      'gmp-place-autocomplete': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        placeholder?: string;
      };
    }
  }
}

// Enums and Constants
const DAYS_OF_WEEK = [
  { id: 'Mon', label: 'Mon' },
  { id: 'Tue', label: 'Tue' },
  { id: 'Wed', label: 'Wed' },
  { id: 'Thu', label: 'Thu' },
  { id: 'Fri', label: 'Fri' },
  { id: 'Sat', label: 'Sat' },
  { id: 'Sun', label: 'Sun' },
];

const STOP_TYPES = [
  { id: 'rest', label: 'Rest Stop' },
  { id: 'bus', label: 'Bus Stop' },
  { id: 'ev', label: 'EV Charging' },
];

// Zod Schema
const routeSchema = z.object({
  fromCity: z.string().min(1, 'Origin is required'),
  toCity: z.string().min(1, 'Destination is required'),
  departureTime: z.string().min(1, 'Departure time is required'),
  arrivalTime: z.string().min(1, 'Arrival time is required'),
  price: z.number().min(0, 'Price must be positive'),
  distance: z.number().min(0),
  duration: z.number().min(0),
  schedule: z.object({
    isRecurring: z.boolean(),
    days: z.array(z.string()),
    startDate: z.string().optional(), // Using string for date input
    endDate: z.string().optional(),
  }),
  stops: z.array(z.object({
    id: z.string(),
    type: z.enum(['rest', 'bus', 'ev']),
    name: z.string().min(1, 'Stop name is required'),
    location: z.object({
      lat: z.number(),
      lng: z.number(),
      address: z.string().optional(),
    }),
    duration: z.number().optional(),
  })),
  rates: z.object({
    luggage: z.number().min(0),
    bag: z.number().min(0),
  }),
  isActive: z.boolean().optional(),
});

type RouteFormData = z.infer<typeof routeSchema>;

interface RouteFormProps {
  route?: Route;
  onSubmit: (data: CreateRouteForm | UpdateRouteForm) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export const RouteForm: React.FC<RouteFormProps> = ({
  route,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const isEditing = !!route;
  const [isMapsLoaded, setIsMapsLoaded] = useState(false);

  useEffect(() => {
    const checkGoogleMaps = () => {
      if (typeof window.google !== 'undefined' && typeof window.google.maps !== 'undefined') {
        setIsMapsLoaded(true);
        return true;
      }
      return false;
    };

    if (!checkGoogleMaps()) {
      const intervalId = setInterval(() => {
        if (checkGoogleMaps()) {
          clearInterval(intervalId);
        }
      }, 100);
      return () => clearInterval(intervalId);
    }
  }, []);

  const fromInputRef = useRef<any>(null);
  const toInputRef = useRef<any>(null);
  const [distanceInfo, setDistanceInfo] = useState<{ text: string; value: number } | null>(null);
  const [durationInfo, setDurationInfo] = useState<{ text: string; value: number } | null>(null);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RouteFormData>({
    defaultValues: route
      ? {
        ...route,
        schedule: {
          ...route.schedule,
          startDate: route.schedule.startDate ? new Date(route.schedule.startDate).toISOString().split('T')[0] : '',
          endDate: route.schedule.endDate ? new Date(route.schedule.endDate).toISOString().split('T')[0] : '',
        },
      }
      : {
        fromCity: '',
        toCity: '',
        departureTime: '08:00',
        arrivalTime: '12:00',
        price: 0,
        distance: 0,
        duration: 0,
        schedule: {
          isRecurring: false,
          days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
          startDate: '',
          endDate: '',
        },
        stops: [],
        rates: { luggage: 0, bag: 0 },
        isActive: true,
      },
  });

  const { fields: stops, append: appendStop, remove: removeStop } = useFieldArray({
    control,
    name: 'stops',
  });

  const watchSchedule = watch('schedule');

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (!isMapsLoaded) return;

    const fromElement = fromInputRef.current;

    if (fromElement) {
      const listener = async (e: any) => {
        const place = e.detail.place; // gmp-placeselect event
        if (place) {
          // We need to fetch the place details because the event might only have the reference
          await place.fetchFields({ fields: ['displayName', 'location'] });
          const name = place.displayName;
          setValue('fromCity', name);
          calculateDistanceAndDuration();
        }
      };
      fromElement.addEventListener('gmp-placeselect', listener);
      return () => {
        fromElement.removeEventListener('gmp-placeselect', listener);
      };
    }
  }, [isMapsLoaded, setValue]);

  useEffect(() => {
    if (!isMapsLoaded) return;

    const toElement = toInputRef.current;

    if (toElement) {
      const listener = async (e: any) => {
        const place = e.detail.place;
        if (place) {
          await place.fetchFields({ fields: ['displayName', 'location'] });
          const name = place.displayName;
          setValue('toCity', name);
          calculateDistanceAndDuration();
        }
      };
      toElement.addEventListener('gmp-placeselect', listener);
      return () => {
        toElement.removeEventListener('gmp-placeselect', listener);
      };
    }
  }, [isMapsLoaded, setValue]);

  const calculateDistanceAndDuration = () => {
    if (!isMapsLoaded) return;

    // For PlaceAutocompleteElement, we might need a different way to get the value if it's not bound to the input
    // But since we set the value in the form, we can just use the form values or the element's value if we track it.
    // However, the DistanceMatrixService needs strings or LatLng.
    // The previous implementation read from refs.
    // With PlaceAutocompleteElement, we should probably rely on the form state we just set, OR read from the element.
    const origin = watch('fromCity'); // Use form state which is updated by the event listener
    const destination = watch('toCity');

    if (!origin || !destination) return;

    const service = new window.google.maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [origin],
        destinations: [destination],
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (response: any, status: any) => {
        if (status === 'OK' && response.rows[0].elements[0].status === 'OK') {
          const element = response.rows[0].elements[0];
          setDistanceInfo({ text: element.distance.text, value: element.distance.value / 1000 }); // value in meters -> km
          setDurationInfo({ text: element.duration.text, value: Math.round(element.duration.value / 60) }); // value in seconds -> minutes

          setValue('distance', parseFloat((element.distance.value / 1000).toFixed(2)));
          setValue('duration', Math.round(element.duration.value / 60));
        }
      }
    );
  };

  const onFormSubmit = async (data: RouteFormData) => {
    const submitData: CreateRouteForm | UpdateRouteForm = {
      ...data,
      schedule: {
        ...data.schedule,
        startDate: data.schedule.startDate ? new Date(data.schedule.startDate) : undefined,
        endDate: data.schedule.endDate ? new Date(data.schedule.endDate) : undefined,
      },
    };
    await onSubmit(submitData);
  };

  const toggleDay = (dayId: string) => {
    const currentDays = watchSchedule.days || [];
    const newDays = currentDays.includes(dayId)
      ? currentDays.filter(d => d !== dayId)
      : [...currentDays, dayId];
    setValue('schedule.days', newDays);
  };

  const handleAddStop = () => {
    appendStop({
      id: crypto.randomUUID(),
      type: 'rest',
      name: '',
      location: { lat: 0, lng: 0, address: '' },
      duration: 15,
    });
  };

  // Logic to merge refs for react-hook-form and local refs

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white z-10 border-b">
          <CardTitle>{isEditing ? 'Edit Route' : 'Add New Route'}</CardTitle>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-8">
            {/* Route Details */}
            <section>
              <h3 className="text-lg font-medium text-secondary-900 mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-primary-600" />
                Origin & Destination
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">From City</label>
                  {/* @ts-ignore */}
                  <gmp-place-autocomplete
                    ref={fromInputRef}
                    placeholder="Enter origin city"
                    className="block w-full rounded-lg border border-secondary-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                  </gmp-place-autocomplete>
                  {/* Hidden input to register with react-hook-form properly for validation/submission if needed, 
                      though we are setting value manually. 
                      Actually, we should keep the input synced or just use the setValue.
                      Since we removed the input, 'fromRegistered' won't work on the custom element directly for typing. 
                      So we rely on setValue in the event listener and maybe a hidden input for form submission if we weren't using controlled state?
                      But wait, calculateDistanceAndDuration() relies on the value.
                  */}
                  <input type="hidden" {...register('fromCity')} />

                  {errors.fromCity && <p className="mt-1 text-sm text-red-600">{errors.fromCity.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">To City</label>
                  {/* @ts-ignore */}
                  <gmp-place-autocomplete
                    ref={toInputRef}
                    placeholder="Enter destination city"
                    className="block w-full rounded-lg border border-secondary-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                  </gmp-place-autocomplete>
                  <input type="hidden" {...register('toCity')} />
                  {errors.toCity && <p className="mt-1 text-sm text-red-600">{errors.toCity.message}</p>}
                </div>
              </div>

              {/* Distance Info Display */}
              {(distanceInfo || durationInfo) && (
                <div className="mt-4 p-3 bg-secondary-50 rounded-lg flex gap-6 text-sm text-secondary-700">
                  {distanceInfo && (
                    <span><strong>Total Distance:</strong> {distanceInfo.text}</span>
                  )}
                  {durationInfo && (
                    <span><strong>Est. Duration:</strong> {durationInfo.text}</span>
                  )}
                </div>
              )}
            </section>

            {/* Timings */}
            <section>
              <h3 className="text-lg font-medium text-secondary-900 mb-4 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-primary-600" />
                Timings
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Input
                  label="Departure Time"
                  type="time"
                  {...register('departureTime')}
                  error={errors.departureTime?.message}
                />
                <Input
                  label="Arrival Time"
                  type="time"
                  {...register('arrivalTime')}
                  error={errors.arrivalTime?.message}
                />
              </div>
            </section>

            {/* Schedule */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-secondary-900 flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-primary-600" />
                  Schedule
                </h3>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    {...register('schedule.isRecurring')}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-secondary-700">Recurring Schedule</span>
                </label>
              </div>

              {watch('schedule.isRecurring') ? (
                <div className="p-4 border border-secondary-200 rounded-lg bg-secondary-50">
                  <label className="block text-sm font-medium text-secondary-700 mb-3">Operating Days</label>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {DAYS_OF_WEEK.map(day => (
                      <button
                        key={day.id}
                        type="button"
                        onClick={() => toggleDay(day.id)}
                        className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${watchSchedule.days?.includes(day.id)
                          ? 'bg-primary-600 text-white'
                          : 'bg-white border border-secondary-300 text-secondary-600 hover:bg-secondary-100'
                          }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Start Date (Optional)"
                      type="date"
                      {...register('schedule.startDate')}
                    />
                    <Input
                      label="End Date (Optional)"
                      type="date"
                      {...register('schedule.endDate')}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-secondary-500 italic">One-time trip schedule.</p>
              )}
            </section>

            {/* Stops */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-secondary-900 flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-primary-600" />
                  Stops
                </h3>
                <Button type="button" variant="outline" size="sm" onClick={handleAddStop}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Stop
                </Button>
              </div>

              <div className="space-y-3">
                {stops.map((field, index) => (
                  <div key={field.id} className="flex gap-4 items-start p-3 border border-secondary-200 rounded-lg">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-secondary-500 mb-1">Type</label>
                        <select
                          {...register(`stops.${index}.type`)}
                          className="block w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        >
                          {STOP_TYPES.map(type => (
                            <option key={type.id} value={type.id}>{type.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-secondary-500 mb-1">Name</label>
                        <input
                          {...register(`stops.${index}.name`)}
                          placeholder="Stop Name"
                          className="block w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        {errors.stops?.[index]?.name && <p className="text-xs text-red-600 mt-1">{errors.stops[index]?.name?.message}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-secondary-500 mb-1">Duration (min)</label>
                        <input
                          type="number"
                          {...register(`stops.${index}.duration`, { valueAsNumber: true })}
                          placeholder="15"
                          className="block w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeStop(index)}
                      className="text-red-500 hover:text-red-700 mt-6"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {stops.length === 0 && (
                  <div className="text-center py-4 bg-secondary-50 rounded-lg border border-dashed border-secondary-300 text-secondary-500 text-sm">
                    No stops added. Direct route.
                  </div>
                )}
              </div>
            </section>

            {/* Rates */}
            <section>
              <h3 className="text-lg font-medium text-secondary-900 mb-4 flex items-center">
                <DollarSign className="w-5 h-5 mr-2 text-primary-600" />
                Pricing & Rates
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input
                  label="Ticket Price (₵)"
                  type="number"
                  step="0.01"
                  {...register('price', { valueAsNumber: true })}
                  error={errors.price?.message}
                />
                <Input
                  label="Luggage Rate (₵)"
                  type="number"
                  step="0.01"
                  {...register('rates.luggage', { valueAsNumber: true })}
                  error={errors.rates?.luggage?.message}
                />
                <Input
                  label="Extra Bag Rate (₵)"
                  type="number"
                  step="0.01"
                  {...register('rates.bag', { valueAsNumber: true })}
                  error={errors.rates?.bag?.message}
                />
              </div>
            </section>

            {isEditing && (
              <div className="pt-4 border-t">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    {...register('isActive')}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-secondary-700">Active Route</span>
                </label>
              </div>
            )}

            <div className="flex space-x-3 pt-6 border-t">
              <Button
                type="submit"
                className="flex-1"
                loading={loading}
                disabled={loading}
              >
                {isEditing ? 'Update Route' : 'Create Route'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onCancel}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
